import { useState, useEffect } from "react";
import { Traveler, BRAND, ProductType } from "../../types";
import { TravelerEditModal } from "../TravelerEditModal";
import {
  fetchUserTravelerDetail,
  createUserTraveler,
  updateUserTraveler,
  deleteUserTraveler,
  setDefaultUserTraveler,
  type UserTravelerItem,
} from "@/api/user";
import { toastSuccess, toastError } from "@/utils/toast";

interface TravelersListProps {
  productType: ProductType;
  travelers: Traveler[];
  onTravelersChange: (travelers: Traveler[]) => void;
}

function newTravelerDraft(productType: ProductType, list: Traveler[]): Traveler {
  const isCar = productType === "car";
  const noDriverYet = !list.some((t) => t.isDriver);
  return {
    name: "",
    type: "成人",
    idCard: "",
    ...(isCar && noDriverYet ? { isDriver: true as const } : {}),
  };
}

export function TravelersList({
  productType,
  travelers,
  onTravelersChange,
}: TravelersListProps) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  // ── 出行人详情弹窗状态 ──────────────────────────
  const [detailData, setDetailData] = useState<UserTravelerItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── 保存 loading（新增出行人时）────────────────────
  const [saving, setSaving] = useState(false);

  // 弹窗打开时隐藏滚动条
  useEffect(() => {
    if (editingIdx !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [editingIdx]);

  const handleSave = async (traveler: Traveler) => {
    // 新增出行人 → 先调接口再更新本地
    if (editingIdx === -1) {
      if (!traveler.name.trim() || !traveler.idCard.trim()) return;
      setSaving(true);
      try {
        const result = await createUserTraveler({
          name: traveler.name.trim(),
          card_type: "1",
          card_no: traveler.idCard.trim(),
          is_default: traveler.isDefault ? "1" : "0",
        });
        toastSuccess(result.message);
        // 勾选了设为默认 → 调设默认接口
        if (traveler.isDefault) {
          try { await setDefaultUserTraveler(result.id); } catch (_) { /* 静默失败 */ }
        }
        const base = traveler.isDriver
          ? travelers.map((t) => ({ ...t, isDriver: false }))
          : travelers;
        const withId = { ...traveler, travelerId: result.id };
        onTravelersChange([...base, withId]);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "添加出行人失败";
        toastError(msg);
        return; // 失败时不关闭弹窗，让用户重试
      } finally {
        setSaving(false);
      }
    } else if (editingIdx !== null) {
      const existing = travelers[editingIdx];
      // 有 travelerId 才调编辑接口，否则仅本地更新
      if (existing.travelerId) {
        setSaving(true);
        try {
          const result = await updateUserTraveler({
            id: existing.travelerId,
            name: traveler.name.trim(),
            card_type: "1",
            card_no: traveler.idCard.trim(),
            is_default: traveler.isDefault ? "1" : "0",
          });
          toastSuccess(result.message);
          // 勾选了设为默认 → 调设默认接口
          if (traveler.isDefault) {
            try { await setDefaultUserTraveler(existing.travelerId); } catch (_) { /* 静默失败 */ }
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : "更新出行人失败";
          toastError(msg);
          return;
        } finally {
          setSaving(false);
        }
      }
      let next = travelers.map((t, i) => (i === editingIdx ? traveler : t));
      if (traveler.isDriver) {
        next = next.map((t, i) => ({
          ...t,
          isDriver: i === editingIdx,
        }));
      }
      onTravelersChange(next);
    }
    setEditingIdx(null);
  };

  /** 删除指定位置的出行人 */
  const handleDelete = async (index: number) => {
    const target = travelers[index];
    // 有 travelerId 才调删除接口
    if (target.travelerId) {
      try {
        const msg = await deleteUserTraveler(target.travelerId);
        toastSuccess(msg);
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : "删除出行人失败";
        toastError(errMsg);
        return; // 接口失败不删除本地数据
      }
    }
    onTravelersChange(travelers.filter((_, i) => i !== index));
  };

  /** 查看出行人详情 */
  const handleViewDetail = async (index: number, travelerId?: number) => {
    if (!travelerId) return;
    setDetailLoading(true);
    try {
      const data = await fetchUserTravelerDetail(travelerId);
      setDetailData(data);
    } catch (err) {
      console.error("获取出行人详情失败:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  /** 设置默认出行人 */
  const handleSetDefault = async () => {
    if (!detailData || detailData.is_default === 1) return;
    try {
      const msg = await setDefaultUserTraveler(detailData.id);
      toastSuccess(msg);
      setDetailData({ ...detailData, is_default: 1 });
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "设置默认失败";
      toastError(errMsg);
    }
  };

  return (
    <>
      <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-gray-900">
            <i
              className="ri-group-line mr-1.5"
              style={{ color: BRAND, fontSize: 15 }}
            />
            出行人员 ({travelers.length}人)
          </div>
          <button
            onClick={() => setEditingIdx(-1)}
            className="text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
            style={{ color: BRAND }}
          >
            <i className="ri-add-line" style={{ fontSize: 13 }} />
            添加
          </button>
        </div>
        {travelers.length === 0 ? (
          <div className="py-8 text-center">
            <i
              className="ri-user-add-line"
              style={{ fontSize: 40, color: "#D1D5DB" }}
            />
            <div className="text-sm text-gray-400 mt-2">暂无出行人员</div>
            <button
              onClick={() => setEditingIdx(-1)}
              className="mt-3 px-4 py-2 rounded-xl text-xs font-medium text-white cursor-pointer"
              style={{ background: BRAND }}
            >
              添加出行人
            </button>
          </div>
        ) : (
          <div className="space-y-0">
            {travelers.map((t, i) => {
              const nameTrim = t.name?.trim() ?? "";
              const avatarChar =
                nameTrim.length >= 2
                  ? nameTrim[1]
                  : nameTrim[0] || "?";
              const idUploaded = Boolean(t.idCardFront && t.idCardBack);
              const licenseUploaded = Boolean(
                t.licenseFront && t.licenseBack,
              );
              return (
              <div
                key={i}
                className="flex items-center justify-between py-3 cursor-pointer active:bg-gray-50"
                style={{
                  borderBottom:
                    i < travelers.length - 1 ? "1px solid #F5F5F5" : "none",
                }}
                onClick={() => handleViewDetail(i, t.travelerId)}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{
                      background: t.isDriver
                        ? BRAND
                        : `${BRAND}${i < 2 ? "99" : "55"}`,
                    }}
                  >
                    {avatarChar}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-800">
                        {t.name}
                      </span>
                      {t.isDriver && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                          style={{ background: "#FFF3EE", color: BRAND }}
                        >
                          主驾
                        </span>
                      )}
                      {t.isDefault && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                          style={{ background: "#FFF3EE", color: BRAND }}
                        >
                          默认
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs text-gray-400">{t.type}</span>
                      {t.idCard && (
                        <span className="text-xs text-gray-300">·</span>
                      )}
                      {t.idCard && (
                        <span className="text-xs text-gray-400">
                          {t.idCard}
                        </span>
                      )}
                    </div>
                    {/* 证件状态 */}
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="text-xs flex items-center gap-0.5"
                        style={{ color: idUploaded ? "#10B981" : "#9CA3AF" }}
                      >
                        <i
                          className={
                            idUploaded
                              ? "ri-checkbox-circle-fill"
                              : "ri-checkbox-blank-circle-line"
                          }
                          style={{ fontSize: 11 }}
                        />
                        身份证
                      </span>
                      {productType === "car" && t.isDriver && (
                        <span
                          className="text-xs flex items-center gap-0.5"
                          style={{
                            color: licenseUploaded ? "#10B981" : "#EF4444",
                          }}
                        >
                          <i
                            className={
                              licenseUploaded
                                ? "ri-checkbox-circle-fill"
                                : "ri-error-warning-line"
                            }
                            style={{ fontSize: 11 }}
                          />
                          驾照{licenseUploaded ? "" : "（必传）"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingIdx(i); }}
                    className="text-xs px-3 py-1.5 rounded-xl cursor-pointer whitespace-nowrap font-medium"
                    style={{ background: "#F5F5F5", color: "#555" }}
                  >
                    编辑
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(i); }}
                    className="text-xs px-3 py-1.5 rounded-xl cursor-pointer whitespace-nowrap font-medium"
                    style={{ background: "#FEF2F2", color: "#EF4444" }}
                  >
                    删除
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>

      {editingIdx !== null && (
        <TravelerEditModal
          traveler={
            editingIdx === -1
              ? newTravelerDraft(productType, travelers)
              : travelers[editingIdx]
          }
          orderType={productType === "car" ? "car" : "hotel"}
          isAdd={editingIdx === -1}
          saving={saving}
          onSave={handleSave}
          onClose={() => setEditingIdx(null)}
        />
      )}

      {/* 出行人详情弹窗 */}
      {detailData && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.4)" }}
          onClick={() => setDetailData(null)}
        >
          <div
            className="w-full max-w-lg rounded-t-3xl p-5 max-h-[80vh] overflow-y-auto"
            style={{ background: "#fff" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 标题栏 */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">出行人详情</h3>
              <button
                onClick={() => setDetailData(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                style={{ background: "#F5F5F5" }}
              >
                <i className="ri-close-line text-gray-500" style={{ fontSize: 18 }} />
              </button>
            </div>

            {/* 详情内容 */}
            <div className="space-y-3">
              {/* 姓名 + 关系标签 */}
              <div className="flex items-center gap-2">
                <span
                  className="text-lg font-bold text-gray-900"
                  style={{ color: BRAND }}
                >
                  {detailData.name}
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: "#FFF7ED", color: "#C2410C" }}
                >
                  {detailData.role_type_text || "-"}
                </span>
                {detailData.is_default === 1 && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: "#EEF2FF", color: "#4338CA" }}
                  >
                    默认
                  </span>
                )}
              </div>

              <div className="h-px" style={{ background: "#F5F5F5" }} />

              {/* 证件信息 */}
              <div>
                <div className="text-xs text-gray-400 mb-1">证件类型</div>
                <div className="text-sm text-gray-800">
                  {detailData.card_type_text || "-"}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-400 mb-1">证件号码</div>
                <div className="text-sm text-gray-800 font-mono">
                  {detailData.card_no || "-"}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-400 mb-1">手机号码</div>
                <div className="text-sm text-gray-800">{detailData.phone || "-"}</div>
              </div>

              <div className="h-px" style={{ background: "#F5F5F5" }} />

              {/* 证件图片 */}
              {(detailData.id_card_front || detailData.id_card_back) && (
                <>
                  <div className="text-xs text-gray-400 mb-2">身份证照片</div>
                  <div className="flex gap-3">
                    {detailData.id_card_front ? (
                      <img
                        src={detailData.id_card_front}
                        alt="身份证正面"
                        className="w-36 h-24 object-cover rounded-xl border"
                        style={{ borderColor: "#E5E7EB" }}
                      />
                    ) : (
                      <div className="w-36 h-24 rounded-xl bg-gray-100 flex items-center justify-center">
                        <span className="text-xs text-gray-300">无正面照</span>
                      </div>
                    )}
                    {detailData.id_card_back ? (
                      <img
                        src={detailData.id_card_back}
                        alt="身份证反面"
                        className="w-36 h-24 object-cover rounded-xl border"
                        style={{ borderColor: "#E5E7EB" }}
                      />
                    ) : (
                      <div className="w-36 h-24 rounded-xl bg-gray-100 flex items-center justify-center">
                        <span className="text-xs text-gray-300">无反面照</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {!detailData.id_card_front && !detailData.id_card_back && (
                <div className="py-6 text-center">
                  <i
                    className="ri-image-line"
                    style={{ fontSize: 32, color: "#D1D5DB" }}
                  />
                  <div className="text-xs text-gray-400 mt-1">
                    暂无证件照片
                  </div>
                </div>
              )}

              <div className="h-px" style={{ background: "#F5F5F5" }} />

              {/* 创建时间 */}
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-gray-400">添加时间</span>
                <span className="text-xs text-gray-500">{detailData.add_time_text || "-"}</span>
              </div>

              {/* 设为默认按钮 */}
              {detailData.is_default !== 1 && (
                <button
                  onClick={handleSetDefault}
                  className="w-full mt-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer"
                  style={{ background: "#EEF2FF", color: "#4338CA" }}
                >
                  <i className="ri-star-line mr-1" />
                  设为默认出行人
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 加载中遮罩 */}
      {detailLoading && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.8)" }}
        >
          <div className="flex flex-col items-center gap-2">
            <div
              className="w-8 h-8 border-2 rounded-full animate-spin"
              style={{ borderTopColor: BRAND, borderColor: "#E5E7EB transparent transparent transparent" }}
            />
            <span className="text-xs text-gray-400">加载详情...</span>
          </div>
        </div>
      )}
    </>
  );
}
